import type { CoveragePackageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import type {
    AllCoverablesOnsaDTO,
    CoverableOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import type {
    AdultAccidentOnsaDTO,
    ChildAccidentOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/accident';
import type { CarOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/motor';
import type { DogOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/pet';
import { calculateAge, convertDateToLocalDateDTO } from '../../../utils/dateUtil';
import { getOnsaCoverable } from '../../../utils/coverablesUtil';

interface PackageProps {
    packageName: string;
    packageNameToDisplay: string;
}

interface PackageGroup {
    packageGroupName: string;
    packages?: PackageProps[];
    defaultPackageName: string;
}

interface PackagesToDisplay {
    packages: CoveragePackageDTO[];
    defaultPackageName: string;
}

interface PackageGroupParams {
    shouldCoverCriticalIllness?: boolean;
}

const getPackageGroups = (
    coverable: CoverableOnsaDTO,
    additionalParams?: PackageGroupParams
): PackageGroup[] => {
    switch (coverable.flavourName) {
        case 'Hundeforsikring': {
            const dog = coverable as DogOnsaDTO;
            const isOldDog =
                calculateAge(dog?.dateOfBirth ?? convertDateToLocalDateDTO(new Date())) >= 7;
            return [
                {
                    packageGroupName: 'Default',
                    packages:
                        dog?.dogUseId === 'Police' && !isOldDog
                            ? [{ packageName: 'Basis', packageNameToDisplay: 'Tjenestehund' }]
                            : undefined,
                    defaultPackageName:
                        isOldDog || dog?.dogUseId === 'Police' ? 'Basis' : 'Standard'
                }
            ];
        }
        case 'Børneforsikring': {
            const shouldCoverCoverage =
                (coverable as ChildAccidentOnsaDTO).shouldCoverCriticalIllness ??
                additionalParams?.shouldCoverCriticalIllness;
            return [
                {
                    packageGroupName: 'Default',
                    defaultPackageName: shouldCoverCoverage ? 'Premium' : 'Standard'
                }
            ];
        }
        case 'Bilforsikring':
            return [
                {
                    packageGroupName: 'Default',
                    defaultPackageName: 'Standard'
                },
                {
                    packageGroupName: 'Liability',
                    defaultPackageName: 'Standard'
                }
            ];
        case 'Katteforsikring':
        case 'Ulykkesforsikring':
        case 'Ulykkesforsikring til unge':
        case 'Indboforsikring':
        case 'Indboforsikring til unge':
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
            return [
                {
                    packageGroupName: 'Default',
                    defaultPackageName: 'Standard'
                }
            ];
        default:
            return [];
    }
};

const getPackagesConfig = (
    coverable: CoverableOnsaDTO,
    packages: CoveragePackageDTO[],
    additionalParams?: PackageGroupParams
): PackagesToDisplay => {
    const packageGroup = getPackageGroups(coverable, additionalParams).find(
        (group) => group.packageGroupName === (coverable.packageGroupName ?? 'Default')
    );
    if (!packageGroup) return { packages, defaultPackageName: '' };

    const coveragePackages: CoveragePackageDTO[] = [];

    packages.forEach((pack) => {
        const packageDisplayName = packageGroup.packages?.find(
            (packProps) => pack.name === packProps.packageName
        )?.packageNameToDisplay;

        coveragePackages.push({
            ...pack,
            name: packageDisplayName ?? pack.name
        });
    });

    if (coveragePackages.length === 0) {
        return { packages, defaultPackageName: '' };
    }

    const sortedPackages = ['Premium', 'Standard', 'Basis'];
    coveragePackages.sort(
        (pack1, pack2) => sortedPackages.indexOf(pack1.name) - sortedPackages.indexOf(pack2.name)
    );

    return {
        packages: coveragePackages,
        defaultPackageName: packageGroup.defaultPackageName
    };
};

const shouldShowModal = (
    coverables: AllCoverablesOnsaDTO,
    selectedPackage: CoveragePackageDTO,
    flavourName: string,
    fixedId: string
): boolean => {
    const coverable = getOnsaCoverable(coverables, flavourName, fixedId);
    switch (flavourName) {
        case 'Ulykkesforsikring': {
            return (
                (coverable as AdultAccidentOnsaDTO).selectedEmployment?.publicId === '1488' &&
                selectedPackage.coverages.find(
                    (coverage) => coverage.coverageId === 'ACCImmediateCompensationCov'
                ) !== undefined
            );
        }
        default:
            return false;
    }
};

const shouldDisableNavigation = (coverable: CoverableOnsaDTO): boolean => {
    switch (coverable.flavourName) {
        case 'Bilforsikring': {
            return (coverable as CarOnsaDTO).yearlyMileage === undefined;
        }
        default:
            return false;
    }
};

export { getPackagesConfig, shouldShowModal, shouldDisableNavigation };
